import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Button } from '@mui/material';
import ErrorHandlingLayout from 'app/shared/componentsRestyled/common/ErrorHandlingLayout';
import PageBlock from 'app/shared/componentsRestyled/common/PageBlock';
import { AddBoxIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import { TableCDS } from 'app/shared/componentsRestyled/coreDesignSystem/Table';
import { WithLoadingPropTypes } from 'hocs/withLoading';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useErrorState from 'hooks/useErrorState';
import useSort from 'hooks/useSort';

import { getDocumentExpirationDatesAction } from 'store/underwriting/actions';
import { selectPreparedDocumentExpirationDates } from 'store/underwriting/selectors';

import { UnderwritingTestIds } from '../Underwriting.testIds';

import { getTableConfig } from './DocumentExpirationDates.config';
import { defaultOrder, newRecordInitialValues } from './DocumentExpirationDates.constants';
import { processData } from './DocumentExpirationDates.utils';

const DocumentExpirationDates = ({ isLoading, setIsLoading }) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();

  const documentExpirationDates = useSelector(selectPreparedDocumentExpirationDates);

  const { order, onRequestSort } = useSort(defaultOrder);
  const { isError, setError, resetError } = useErrorState();

  const [dataToEdit, setDataToEdit] = useState(null);

  const isEditMode = !!dataToEdit;

  const tableConfig = useMemo(
    () => getTableConfig({ setDataToEdit, isEditMode }),
    [setDataToEdit, isEditMode],
  );

  const processedData = useMemo(
    () => processData({ data: documentExpirationDates, order, dataToEdit, setDataToEdit }),
    [dataToEdit, documentExpirationDates, order, setDataToEdit],
  );

  const onAddClick = useCallback(() => {
    setDataToEdit(newRecordInitialValues);
  }, []);

  const fetchData = useCallback(() => {
    setIsLoading(true);
    resetError();

    dispatch(getDocumentExpirationDatesAction({ applicationId }))
      .catch(setError)
      .finally(() => setIsLoading(false));
  }, [dispatch, setIsLoading, setError, resetError, applicationId]);

  useEffectSafeStrictMode(() => {
    fetchData();
  }, [fetchData]);

  return (
    <PageBlock
      withAction
      withDivider
      actionContent={
        <Button
          data-testid={UnderwritingTestIds.DOC_EXPIRATION_ADD_BUTTON}
          disabled={isLoading || isError || isEditMode}
          startIcon={<AddBoxIcon />}
          onClick={onAddClick}
        >
          Add
        </Button>
      }
      title="Document Expiration Dates"
    >
      <ErrorHandlingLayout isError={isError} onClick={fetchData}>
        <TableCDS
          withNoAvailableMessage
          config={tableConfig}
          data={processedData}
          data-testid={UnderwritingTestIds.DOC_EXPIRATION_TABLE}
          isLoading={isLoading || isError}
          isSortDisabled={isEditMode}
          order={order}
          skeletonRows={5}
          onRequestSort={onRequestSort}
        />
      </ErrorHandlingLayout>
    </PageBlock>
  );
};

DocumentExpirationDates.propTypes = {
  ...WithLoadingPropTypes,
};

export default memo(DocumentExpirationDates);
